import { Col, Row, Label, CardBody, Input } from 'reactstrap';
import { api } from '../../../config';
import logger from '../../../utils/logger';
import { useState, useEffect } from 'react';
import { SkeletonTable } from '../../../Components/Common/Skeleton';
import notify, { MESSAGES } from '../../../utils/notify';
import { updateAlphaHpsRow } from './HpsParcel/nonLpg/utils';
import { resolveInformationPrompt as informationLabel } from '../utils/informationPrompt';
import { isSingleAlphaProductName } from '../../../shared/offer-domain/constants/product';
import '../HpsView.css';

const isFobDetail = details =>
  String(details?.incoterm ?? '')
    .trim()
    .toUpperCase() === 'FOB';

// Alpha HPS belum tentu sudah diunggah; tampilkan '-' supaya sel kosong tidak
// terbaca sebagai nilai nol.
const alphaCell = value =>
  value === null || value === undefined || value === '' ? '-' : value;

// Satu entri listData = satu baris, kecuali FOB yang dipecah
// menjadi satu baris per incotermFOBLocations.
const buildIncotermRows = (item, details) => {
  const base = {
    id: item.id,
    // Kunci update alpha: listData[].id = QuantityInformationDetail.Id
    // (bukan item.id, itu id grup).
    quantityInformationDetailId: details?.id ?? null,
    incotermLocationId: null,
    locationSlot: null,
    quantityInformationId: details?.quantityInformationId ?? null,
    quantityIncotermId: details.quantityIncotermId ?? null,
    quantityLocation2: details.quantityLocation2 ?? null,
    dyed: details.dyed ?? null,
    undyed: details.undyed ?? null,
    dyed2: details.dyed2 ?? null,
    undyed2: details.undyed2 ?? null,
    alpha1: details.alpha1 ?? 0,
    alpha2: details.alpha2 ?? null,
    // Alpha HPS hasil upload parcel. Untuk baris non-FOB nilainya ada di
    // listData (QuantityInformationDetail); baris FOB menimpanya per lokasi
    // di bawah, karena tiap lokasi punya nilainya sendiri.
    alphaDyedHps: details.alphaDyedHps ?? null,
    alphaUndyedHps: details.alphaUndyedHps ?? null,
  };

  if (!isFobDetail(details)) {
    return [
      {
        ...base,
        deliveryBasis: details.deliveryBasis ?? null,
        incoterm: details.incoterm ?? null,
        route: '',
        loadingPort: '',
        parcelSize: details.parcelSize ?? null,
        port1: details.port1 ?? null,
        addAld: details.addAld ?? null,
        parcelSize2: details.parcelSize2 ?? null,
        port2: details.port2 ?? null,
        addAld2: details.addAld2 ?? null,
        parcelSize3: details.parcelSize3 ?? null,
        port3: details.port3 ?? null,
        addAld3: details.addAld3 ?? null,
      },
    ];
  }

  // Kolom Discharge Qty / Port / ALD-ADD sengaja dikosongkan untuk baris FOB.
  const blanks = {
    parcelSize: '',
    port1: '',
    addAld: '',
    parcelSize2: '',
    port2: '',
    addAld2: '',
    parcelSize3: '',
    port3: '',
    addAld3: '',
  };

  const locations = Array.isArray(details.incotermFOBLocations)
    ? details.incotermFOBLocations
    : [];

  // FOB tanpa lokasi: tetap tampilkan satu baris dari listData supaya
  // entrinya tidak hilang diam-diam dari tabel.
  if (!locations.length) {
    return [
      {
        ...base,
        ...blanks,
        deliveryBasis: details.deliveryBasis ?? null,
        incoterm: details.incoterm ?? null,
        route: details.route ?? '',
        loadingPort: '',
      },
    ];
  }

  return locations.map(loc => ({
    ...base,
    ...blanks,
    // Hanya incotermFOBLocations (slot 1) yang dirender.
    incotermLocationId: loc.id ?? null,
    locationSlot: 1,
    deliveryBasis: loc.deliveryBasis ?? null,
    incoterm: loc.incoterm ?? null,
    route: loc.route ?? '',
    loadingPort: loc.name ?? '',
    addAld: loc.addAld ?? '',
    alphaDyedHps: loc.alphaDyedHps ?? null,
    alphaUndyedHps: loc.alphaUndyedHps ?? null,
  }));
};

const parseAlphaInput = value => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const ModeDeliveryTableFragment = ({
  procurementId,
  quantityDeliveryTermId,
  token,
  refreshKey = 0,
  isEditable = false,
  infoPrompt = null,
  productName = null,
}) => {
  // Avtur / High Speed Diesel: satu kolom alpha berjudul "Alpha"
  // (nilai = Alpha Dyed), tanpa kolom Alpha UnDyed.
  const singleAlpha = isSingleAlphaProductName(productName);
  const [qtyModeDeliveryIncoterm, setQtyModeDeliveryIncoterm] = useState({
    items: [],
  });
  const [loaded, setLoaded] = useState(false);
  const [alphaColumns, setAlphaColumns] = useState({
    dyed: true,
    undyed: true,
  });
  const [currentPage] = useState(1);
  const [rowsPerPage] = useState(30);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editValues, setEditValues] = useState({
    alphaDyedHps: '',
    alphaUndyedHps: '',
  });
  const [isSavingAlpha, setIsSavingAlpha] = useState(false);

  // singleAlpha meng-override pilihan dyed/undyed dari API: hanya kolom
  // dyed yang tampil (berjudul "Alpha").
  const effectiveAlphaColumns = singleAlpha
    ? { dyed: true, undyed: false }
    : alphaColumns;

  useEffect(() => {
    // jangan menembak API sebelum seluruh parameter wajibnya tersedia
    if (!procurementId || !quantityDeliveryTermId || !token) {
      setLoaded(true);
      return;
    }

    fetchDeliveryDataDetailIncoterm(
      procurementId,
      quantityDeliveryTermId,
      currentPage,
      rowsPerPage,
      token
    );
  }, [
    procurementId,
    quantityDeliveryTermId,
    currentPage,
    rowsPerPage,
    token,
    refreshKey,
  ]);

  const fetchDeliveryDataDetailIncoterm = async (
    procurementId,
    quantityDeliveryTermId,
    currentPage,
    rowsPerPage,
    token
  ) => {
    try {
      const response = await fetch(
        `${api.API_URL}/procurement/get-delivery-detail?PageNumber=${currentPage}&PageSize=${rowsPerPage}&procurementId=${procurementId}&quantityDeliveryTermId=${quantityDeliveryTermId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      const groups = Array.isArray(result?.data?.data) ? result.data.data : [];
      // A gasoline parcel may ask for only Dyed or only Undyed; parcels saved
      // before that choice show both.
      setAlphaColumns({
        dyed: groups.every(group => group?.isDyed !== false),
        undyed: groups.every(group => group?.isUndyed !== false),
      });
      const items = groups.flatMap(item =>
        (item.listData || []).flatMap(details =>
          buildIncotermRows(item, details)
        )
      );
      setQtyModeDeliveryIncoterm({ items });
    } catch (error) {
      logger.error(
        'Error fetchDeliveryDataDetailIncoterm:',
        error?.response?.status ?? 'no-status',
        error?.message ?? String(error)
      );
    } finally {
      setLoaded(true);
    }
  };

  const handleStartEdit = (data, index) => {
    if (isSavingAlpha) return;
    setEditingRowIndex(index);
    setEditValues({
      alphaDyedHps: data.alphaDyedHps ?? '',
      alphaUndyedHps: data.alphaUndyedHps ?? '',
    });
  };

  const handleCancelEdit = () => {
    if (isSavingAlpha) return;
    setEditingRowIndex(null);
  };

  const handleSaveEdit = async data => {
    const { quantityInformationDetailId, incotermLocationId, locationSlot } =
      data;
    if (!quantityInformationDetailId) {
      notify.warning(
        'This row cannot be updated because its parcel is missing.'
      );
      return;
    }

    const alphaDyedHps = parseAlphaInput(editValues.alphaDyedHps);
    const alphaUndyedHps = parseAlphaInput(editValues.alphaUndyedHps);
    if (alphaDyedHps === undefined || alphaUndyedHps === undefined) {
      notify.warning('Alpha must be a valid number.');
      return;
    }

    setIsSavingAlpha(true);
    try {
      await updateAlphaHpsRow({
        quantityInformationDetailId,
        incotermLocationId,
        locationSlot,
        alphaDyedHps,
        alphaUndyedHps,
      });
      notify.success(MESSAGES.updated);
      setEditingRowIndex(null);
      await fetchDeliveryDataDetailIncoterm(
        procurementId,
        quantityDeliveryTermId,
        currentPage,
        rowsPerPage,
        token
      );
    } catch (error) {
      notify.apiError(error, 'update alpha HPS');
    } finally {
      setIsSavingAlpha(false);
    }
  };

  // Class freeze kanan: Action selalu paling kanan; kolom alpha di kirinya.
  // Shadow pemisah hanya di kolom freeze kanan paling kiri yang tampil.
  const dyedFreezeClass = `freeze-right ${
    effectiveAlphaColumns.undyed ? 'freeze-right-3' : 'freeze-right-2'
  } freeze-right-shadow`;
  const undyedFreezeClass = `freeze-right freeze-right-2${
    effectiveAlphaColumns.dyed ? '' : ' freeze-right-shadow'
  }`;
  const actionFreezeClass = `freeze-right freeze-right-1${
    effectiveAlphaColumns.dyed || effectiveAlphaColumns.undyed
      ? ''
      : ' freeze-right-shadow'
  }`;

  return (
    <Col lg={12}>
      <Row>
        <Col sm={12}>
          <div className='mb-2 mt-3'>
            <Label for='product-name-1' className='form-label fw-semibold'>
              Incoterm
            </Label>
            <CardBody>
              <div className='table-card overflow-x-auto'>
                <table
                  aria-label='Model delivery'
                  className='table table-hover table-centered align-middle table-incoterm-freeze'
                >
                  <thead className='table-light'>
                    <tr>
                      <th
                        scope='col'
                        width='6%'
                        className='freeze-col freeze-col-1'
                      >
                        Parcel Size
                      </th>
                      <th
                        scope='col'
                        width='6%'
                        className='freeze-col freeze-col-2'
                      >
                        Incoterm
                      </th>
                      <th
                        scope='col'
                        width='6%'
                        className='freeze-col freeze-col-3'
                      >
                        Route
                      </th>
                      <th scope='col' width='6%'>
                        Loading Port
                      </th>
                      <th scope='col' width='6%'>
                        Discharge Qty 1
                      </th>
                      <th scope='col' width='6%'>
                        Port 1
                      </th>
                      <th scope='col' className='col-ald-add'>
                        ALD/ADD
                      </th>
                      <th scope='col' width='6%'>
                        Discharge Qty 2
                      </th>
                      <th scope='col' width='9%'>
                        Port 2
                      </th>
                      <th scope='col' className='col-ald-add'>
                        ALD/ADD
                      </th>
                      <th scope='col' width='6%'>
                        Discharge Qty 3
                      </th>
                      <th scope='col' width='9%'>
                        Port 3
                      </th>
                      <th scope='col' className='col-ald-add'>
                        ALD/ADD
                      </th>
                      <th scope='col' width='6%'>
                        Information
                      </th>
                      {effectiveAlphaColumns.dyed && (
                        <th
                          scope='col'
                          width='5%'
                          className={isEditable ? dyedFreezeClass : undefined}
                        >
                          {singleAlpha ? 'Alpha' : 'Alpha Dyed'}
                        </th>
                      )}
                      {effectiveAlphaColumns.undyed && (
                        <th
                          scope='col'
                          width='5%'
                          className={isEditable ? undyedFreezeClass : undefined}
                        >
                          Alpha UnDyed
                        </th>
                      )}
                      {isEditable && (
                        <th
                          scope='col'
                          width='6%'
                          className={`text-center ${actionFreezeClass}`}
                        >
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {!loaded && (
                      <SkeletonTable
                        inline
                        rows={2}
                        columns={
                          14 +
                          Number(effectiveAlphaColumns.dyed) +
                          Number(effectiveAlphaColumns.undyed) +
                          Number(isEditable)
                        }
                      />
                    )}
                    {qtyModeDeliveryIncoterm.items.map((data, index) => {
                      const isEditing = isEditable && editingRowIndex === index;
                      return (
                        <tr key={`${data.id}-${index}`}>
                          <td className='freeze-col freeze-col-1'>
                            {data.deliveryBasis}
                          </td>
                          <td className='freeze-col freeze-col-2'>
                            {data.incoterm}
                          </td>
                          <td className='freeze-col freeze-col-3'>
                            {data.route}
                          </td>
                          <td>{data.loadingPort}</td>
                          <td>{data.parcelSize}</td>
                          <td>{data.port1}</td>
                          <td className='col-ald-add'>{data.addAld}</td>
                          <td>{data.parcelSize2}</td>
                          <td>{data.port2}</td>
                          <td className='col-ald-add'>{data.addAld2}</td>
                          <td>{data.parcelSize3}</td>
                          <td>{data.port3}</td>
                          <td className='col-ald-add'>{data.addAld3}</td>
                          <td>{informationLabel(infoPrompt)}</td>
                          {effectiveAlphaColumns.dyed && (
                            <td
                              className={
                                isEditable ? dyedFreezeClass : undefined
                              }
                            >
                              {isEditing ? (
                                <Input
                                  type='number'
                                  step='any'
                                  bsSize='sm'
                                  style={{ minWidth: '70px' }}
                                  value={editValues.alphaDyedHps}
                                  disabled={isSavingAlpha}
                                  onChange={e =>
                                    setEditValues(prev => ({
                                      ...prev,
                                      alphaDyedHps: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                alphaCell(data.alphaDyedHps)
                              )}
                            </td>
                          )}
                          {effectiveAlphaColumns.undyed && (
                            <td
                              className={
                                isEditable ? undyedFreezeClass : undefined
                              }
                            >
                              {isEditing ? (
                                <Input
                                  type='number'
                                  step='any'
                                  bsSize='sm'
                                  style={{ minWidth: '70px' }}
                                  value={editValues.alphaUndyedHps}
                                  disabled={isSavingAlpha}
                                  onChange={e =>
                                    setEditValues(prev => ({
                                      ...prev,
                                      alphaUndyedHps: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                alphaCell(data.alphaUndyedHps)
                              )}
                            </td>
                          )}
                          {isEditable && (
                            <td className={`text-center ${actionFreezeClass}`}>
                              {isEditing ? (
                                <div className='d-flex justify-content-center gap-1'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-soft-success'
                                    aria-label='Save alpha'
                                    disabled={
                                      isSavingAlpha ||
                                      !data.quantityInformationDetailId
                                    }
                                    onClick={() => handleSaveEdit(data)}
                                  >
                                    {isSavingAlpha ? (
                                      <span
                                        className='spinner-border spinner-border-sm'
                                        role='status'
                                        aria-hidden='true'
                                      ></span>
                                    ) : (
                                      <i className='ri-check-line'></i>
                                    )}
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-soft-secondary'
                                    aria-label='Cancel edit'
                                    disabled={isSavingAlpha}
                                    onClick={handleCancelEdit}
                                  >
                                    <i className='ri-close-line'></i>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-soft-primary'
                                  aria-label='Edit alpha'
                                  disabled={!data.quantityInformationDetailId}
                                  onClick={() => handleStartEdit(data, index)}
                                >
                                  <i className='ri-pencil-line'></i>
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </div>
        </Col>
      </Row>
    </Col>
  );
};

export default ModeDeliveryTableFragment;
